import React, { useCallback, useEffect, useMemo, useState, useContext } from 'react';
import PropTypes from 'prop-types';
import { Grid, Tooltip } from '@mui/material';
import { Typography, Dialog } from '@totalsoft/rocket-ui';
import EditIcon from '@mui/icons-material/Edit';
import InfoIcon from '@mui/icons-material/Info';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { shape, greaterThan, required, stopOnFirstFailure } from '@totalsoft/pure-validations';
import { get, set, useChangeTrackingLens } from '@totalsoft/change-tracking-react';
import { isDirty } from '@totalsoft/change-tracking';
import { emptyString } from 'constants/defaultValues';
import { getErrors, isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { valueOrDefault } from 'utils/functions';
import { useTranslation } from 'react-i18next';
import SplitButton from 'components/common/buttons/SplitButton';
import { UPDATE_INSURANCE_OFFERS } from '../mutations';
import { useMutation } from 'features/common/security/hooks';
import { useSimpleError, useToast, useAuthorization } from 'hooks';
import { useSubscription } from '@apollo/client';
import { INSURANCE_OFFER_DETAIL_UPDATED } from '../subscriptions';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import {
  QuotationDetailAvatar,
  QuotationDetailContainer,
  QuotationDetailSaveButton,
  QuotationDetailTextField,
  QuotationButton
} from './ComparatorStyles';

const validator = shape({
  sourceOfferId: required,
  insuranceAmount: [required, greaterThan(0)] |> stopOnFirstFailure
});

const QuotationDetailComponent = ({ data, onSelectQuotation, globalReadOnly, computing, offerInNegotiation }) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToSelectQuotation = useAuthorization(AuthorizationCodes.hasRightToSelectQuotation, documentTypeId, documentStateId);
  const { t } = useTranslation();
  const showError = useSimpleError();
  const addToast = useToast();
  const { selected } = data;

  const [inEdit, setInEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [insurerMessageOpen, setInsurerMessageOpen] = useState(false);
  const [validation, validate] = useDirtyFieldValidation(validator);

  const [lens, dirtyInfo, resetLens] = useChangeTrackingLens(data);

  const detail = useMemo(() => lens |> get, [lens]);

  const disabled = useMemo(() => !detail.insuranceAmount && (!detail.sourceOfferId || !offerInNegotiation), [detail, offerInNegotiation]);

  const getDisabledStatus = useCallback(
    ({ customValidationFunction, controlName, hasAuthorization } = {}) => {
      return computing || globalReadOnly({ customValidationFunction, controlName, hasAuthorization });
    },
    [globalReadOnly, computing]
  );

  useEffect(() => {
    if ((lens.selected |> get) === selected) return;
    (lens.selected |> set)(selected);
  }, [lens, selected]);

  // #region Save

  const saveDisabled = useMemo(
    () =>
      saving ||
      globalReadOnly({
        controlName: 'Comparator.SelectOffer',
        checkQuotationsStatus: { processingStatus: true, expiredStatus: true }
      }) ||
      !isDirty(dirtyInfo),
    [saving, globalReadOnly, dirtyInfo]
  );

  const [updateInsuranceOffers] = useMutation(UPDATE_INSURANCE_OFFERS, {
    onError: error => {
      setSaving(false);
      showError(error);
    }
  });

  const handleSave = useCallback(() => {
    const input = lens |> get;
    if (!validate(input)) {
      return;
    }

    const { documentId, period, offerDetailId, insuranceAmount } = input;

    setSaving(true);
    updateInsuranceOffers({
      variables: {
        input: {
          documentId,
          period,
          details: [{ offerDetailId, insuranceAmount }]
        }
      }
    });
  }, [lens, validate, updateInsuranceOffers]);

  // #endregion

  const handleSelect = useCallback(() => {
    if (detail.selected) return;
    onSelectQuotation(detail.offerDetailId);
  }, [detail, onSelectQuotation]);

  const handleCloseEditing = useCallback(() => {
    setInEdit(false);
    setTimeout(() => {
      resetLens(data);
    }, 100); /* Reset value at the end of the animation */
  }, [data, resetLens]);

  const handleInsuranceMessageOpenChange = useCallback(value => () => setInsurerMessageOpen(value), []);

  useSubscription(INSURANCE_OFFER_DETAIL_UPDATED, {
    variables: { offerDetailId: detail.offerDetailId },
    skip: !detail.offerDetailId,
    onData: ({
      data: {
        data: { insuranceOfferDetailUpdated }
      }
    }) => {
      const { insuranceAmount } = insuranceOfferDetailUpdated;
      addToast(t('General.Saved'), 'success', { toastId: 'insuranceOfferDetailSavedToast' });
      setSaving(false);
      setInEdit(false);
      resetLens(prev => ({ ...prev, insuranceAmount }));
    }
  });

  const actions = useMemo(
    () => [
      {
        icon: <EditIcon color='primary' fontSize='small' />,
        name: t('Offer.Edit.Comparator.EditQuotation'),
        visible: detail.sourceOfferId && offerInNegotiation,
        handler: () => setInEdit(true)
      },
      {
        icon: <InfoIcon color='primary' fontSize='small' />,
        name: t('Offer.Edit.Comparator.ShowInsurerMessage'),
        visible: !detail.sourceOfferId && Boolean(detail.insurerMessage),
        handler: () => setInsurerMessageOpen(true)
      }
    ],
    [t, detail, offerInNegotiation]
  );

  return (
    <>
      <Grid item xs={6}>
        <QuotationDetailContainer inEdit={inEdit}>
          <SplitButton actions={actions} color={detail.selected ? 'secondary' : 'primary'}>
            <QuotationButton
              id={'Comparator_InsuranceAmountButton_' + detail.offerDetailId}
              tooltip={
                detail.policyGenerationKey ? t('Offer.Edit.Comparator.OfferNumber', { offerNumber: detail.policyGenerationKey }) : ''
              }
              startIcon={
                detail.isDirectCompensation && detail.insuranceAmount ? (
                  <QuotationDetailAvatar>{t('Offer.Edit.Comparator.DirectSettlementCaption')}</QuotationDetailAvatar>
                ) : undefined
              }
              onClick={handleSelect}
              sx={{ width: '100%' }}
              disabled={
                getDisabledStatus({
                  controlName: 'Comparator.SelectOffer',
                  hasAuthorization: hasRightToSelectQuotation
                }) || disabled
              }
              endIcon={detail.selected && <CheckCircleIcon />}
              quotationSelected={detail.selected}
              component={'button'}
            >
              {detail.insuranceAmount
                ? t('DECIMAL2', { number: detail.insuranceAmount }) + ' ' + detail.currencyCode
                : t('Offer.Edit.Comparator.InsuranceAmountNA')}
            </QuotationButton>
          </SplitButton>
          {detail.sourceOfferId && offerInNegotiation && !getDisabledStatus({ controlName: 'Comparator.EditQuotation' }) && (
            <QuotationDetailTextField
              id={`insuranceAmount_${detail.offerDetailId}_DD`}
              isNumeric
              fullWidth
              onChange={lens.insuranceAmount |> set}
              value={lens.insuranceAmount |> get |> valueOrDefault(emptyString)}
              error={!isValid(validation.insuranceAmount)}
              helperText={getErrors(validation.insuranceAmount)}
              startAdornment={
                <Tooltip title={t('Tooltips.Cancel')}>
                  <CloseIcon color={'error'} onClick={handleCloseEditing} />
                </Tooltip>
              }
              endAdornment={
                <QuotationDetailSaveButton
                  id={`save_detailAmount_${detail.offerDetailId}`}
                  onClick={handleSave}
                  size={'small'}
                  disabled={saveDisabled}
                  tooltip={t('Tooltips.Save')}
                >
                  <SaveIcon />
                </QuotationDetailSaveButton>
              }
            />
          )}
        </QuotationDetailContainer>
      </Grid>
      <Dialog
        id={`insurance-message-dialog-${detail.offerDetailId}`}
        fullWidth
        maxWidth={'sm'}
        onClose={handleInsuranceMessageOpenChange(false)}
        open={insurerMessageOpen}
        scroll={'paper'}
        content={<Typography>{detail.insurerMessage}</Typography>}
      />
    </>
  );
};

QuotationDetailComponent.propTypes = {
  data: PropTypes.object.isRequired,
  onSelectQuotation: PropTypes.func.isRequired,
  globalReadOnly: PropTypes.func.isRequired,
  computing: PropTypes.bool.isRequired,
  offerInNegotiation: PropTypes.bool.isRequired
};

export default QuotationDetailComponent;
